/**
 * prescription-configurator.js
 *
 * <prescription-configurator> — custom element controlling the
 * prescription flow, styled after the Meta Rx pattern the person
 * asked to follow: Add to Cart and Buy It Now stay native and visible
 * at all times. This element renders as a collapsed "Add Your
 * Prescription" row (placed near the frame/variant picker, not below
 * Buy Buttons) that expands into the step flow on click, and
 * collapses back into a compact summary once a prescription is saved.
 *
 * Cart submission is NOT triggered by anything inside this element.
 * Instead:
 *   - Add to Cart's native submit is intercepted (capture-phase,
 *     before Dawn's own product-form.js handler runs) ONLY once a
 *     prescription has been saved — otherwise it's untouched and
 *     behaves exactly as stock Dawn.
 *   - Buy It Now (dynamic checkout) is disabled only while a
 *     prescription is actively saved, since it can't carry the second
 *     lens line item; it re-enables the moment the prescription is
 *     removed/edited away.
 *
 * Depends on:
 *   - prescription-lens-data.js  (lens matrix, range classification)
 *   - prescription-validation.js (field-level validation)
 *   - cart-integration.js        (two-item Ajax cart add)
 *   - Dawn's global pub/sub (`subscribe`, `PUB_SUB_EVENTS`), already
 *     loaded theme-wide by global.js — not re-declared here.
 */

// prescription-lens-data.js, prescription-validation.js, and
// cart-integration.js are intentionally NOT statically imported here.
// A static `import './file.js'` has no way to carry a cache-busting
// version string, so the browser/CDN can serve a permanently stale
// copy of those files even after they're edited and saved. Instead,
// each is loaded via dynamic import() using the Shopify-versioned
// asset_url Liquid provides on the element's dataset (see
// snippets/prescription-configurator.liquid) — the same cache-busting
// mechanism the top-level <script src> tag already benefits from.

const STEPS = Object.freeze({
  PRESCRIPTION_TYPE: 'prescription_type', // Single Vision (only option in V1)
  PRESCRIPTION_DETAILS: 'prescription_details', // OD / OS entry
  PD: 'pd',
  LENS_SELECTION: 'lens_selection',
  UPLOAD: 'upload', // required prescription document upload, per Stuart
  REVIEW: 'review',
  OUT_OF_RANGE: 'out_of_range',
});

const STEP_ORDER = [
  STEPS.PRESCRIPTION_TYPE,
  STEPS.PRESCRIPTION_DETAILS,
  STEPS.PD,
  STEPS.LENS_SELECTION,
  STEPS.UPLOAD,
  STEPS.REVIEW,
];

/**
 * Client-side image compression for the prescription upload.
 *
 * Phone-camera prescription photos are routinely 3-8MB, and upload
 * bandwidth (especially mobile/LTE) is often a fraction of download
 * bandwidth — a raw multi-MB photo can take 20-30+ seconds to leave
 * the device. A prescription photo only needs to stay legible, not
 * full sensor resolution, so this downsamples to a max dimension and
 * re-encodes as JPEG before the file ever reaches cart-integration.js.
 *
 * Only applies to image/jpeg and image/png — HEIC and PDF pass
 * through untouched (canvas re-encoding of HEIC isn't reliably
 * supported across browsers, and PDFs aren't safely recompressible
 * client-side without a real PDF library).
 */
async function compressPrescriptionImage(file, { maxDimension = 1600, quality = 0.8 } = {}) {
  if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
    return file;
  }

  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch (e) {
    console.warn('compressPrescriptionImage: could not decode image, uploading original.', e);
    return file;
  }

  const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d').drawImage(bitmap, 0, 0, width, height);
  bitmap.close?.();

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
  if (!blob || blob.size >= file.size) return file; // fall back if encoding failed or didn't help

  const newName = file.name.replace(/\.\w+$/, '') + '.jpg';
  return new File([blob], newName, { type: 'image/jpeg' });
}

function emptyState() {
  return {
    open: false, // collapsed by default — opening IS the "I want a prescription" signal
    step: STEPS.PRESCRIPTION_TYPE,
    prescriptionType: null, // 'single-vision' (only value in V1)
    od: { sphere: '', cylinder: '', axis: '' },
    os: { sphere: '', cylinder: '', axis: '' },
    pdType: null, // 'single' | 'dual'
    pd: '',
    pdRight: '',
    pdLeft: '',
    selectedLensId: null,
    prescriptionFile: null, // File object, required (per Stuart) before Review
    prescriptionFileName: '',
    compressing: false, // true while a selected image is being downsized/re-encoded
    ageConfirmed: false,
    detailsConfirmed: false,
    errors: [],
    savedPayload: null, // set once the customer saves a completed prescription
    savedFile: null, // the raw File object, kept alongside savedPayload (not JSON-safe)
  };
}

if (!customElements.get('prescription-configurator')) {
  customElements.define(
    'prescription-configurator',
    class PrescriptionConfigurator extends HTMLElement {
      async connectedCallback() {
        this.sectionId = this.dataset.section;
        this.productFormId = this.dataset.productFormId;
        this.collection = this.dataset.collection || 'nova'; // 'nova' | 'signature'

        try {
          this.lensVariantMap = JSON.parse(this.dataset.lensVariantMap || '{}');
        } catch (e) {
          console.error('prescription-configurator: could not parse lens variant map.', e);
          this.lensVariantMap = {};
        }

        this.state = emptyState();
        this.submitting = false;

        this.root = this.querySelector('[data-configurator-root]');
        if (!this.root) {
          console.error('prescription-configurator: missing [data-configurator-root] mount point.');
          return;
        }

        try {
          const [lensData, validation, cartIntegration] = await Promise.all([
            import(this.dataset.lensDataUrl),
            import(this.dataset.validationUrl),
            import(this.dataset.cartIntegrationUrl),
          ]);
          this._modules = {
            classifyPrescription: lensData.classifyPrescription,
            getAvailableLensOptions: lensData.getAvailableLensOptions,
            getLensLeadTimeWeeks: lensData.getLensLeadTimeWeeks,
            validatePrescription: validation.validatePrescription,
            validatePd: validation.validatePd,
            validatePrescriptionFile: validation.validatePrescriptionFile,
            sphereStepOptions: validation.sphereStepOptions,
            cylinderStepOptions: validation.cylinderStepOptions,
            axisStepOptions: validation.axisStepOptions,
            addPrescriptionOrderToCart: cartIntegration.addPrescriptionOrderToCart,
          };
        } catch (e) {
          console.error('prescription-configurator: failed to load one or more dependent modules.', e);
          return; // fail soft — no configurator rather than a broken page
        }

        this.currentFrame = this.readCurrentFrameName();
        this.bindVariantChangeListener();
        this.bindNativeSubmitInterception();
        this.render();
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
      }

      // ---- Locating the native buy buttons (read-only lookups; we
      // never hide the whole <product-form> in this architecture) ----

      get productFormElement() {
        const scope = this.closest('product-info') || document;
        return scope.querySelector('product-form');
      }

      get dynamicCheckoutContainer() {
        return this.productFormElement?.querySelector('.shopify-payment-button') || null;
      }

      /**
       * Disables Buy It Now while a prescription is saved — it can't
       * carry the paired lens line item the way Add to Cart can.
       * Hides Shopify's injected button and shows an inline note in
       * its place, rather than trying to reach into the iframe it
       * renders (which isn't reliably scriptable).
       */
      setDynamicCheckoutDisabled(disabled) {
        const container = this.dynamicCheckoutContainer;
        if (!container) return; // theme may have dynamic checkout turned off entirely

        if (disabled) {
          if (!container.hidden) container.hidden = true;
          if (!this._dynamicCheckoutNote) {
            const note = document.createElement('p');
            note.dataset.prescriptionCheckoutNote = '';
            note.textContent = 'Use Add to Cart for prescription orders.';
            container.insertAdjacentElement('afterend', note);
            this._dynamicCheckoutNote = note;
          }
        } else {
          container.hidden = false;
          this._dynamicCheckoutNote?.remove();
          this._dynamicCheckoutNote = null;
        }
      }

      /**
       * Intercepts the native Add to Cart submit, but ONLY when a
       * prescription has been saved. Added as a capture-phase
       * listener on <product-form> itself (an ancestor of the actual
       * <form>), so it runs before Dawn's own bubble-phase submit
       * handler on the <form> — letting us preventDefault() and stop
       * that handler from also firing, without ever touching
       * product-form.js or buy-buttons.liquid.
       */
      bindNativeSubmitInterception() {
        const productForm = this.productFormElement;
        if (!productForm) {
          console.warn('prescription-configurator: <product-form> not found — cannot wire Add to Cart interception.');
          return;
        }
        productForm.addEventListener(
          'submit',
          (event) => {
            if (!this.state.savedPayload) return; // no prescription active: let Dawn handle it untouched
            event.preventDefault();
            event.stopImmediatePropagation();
            this.submitPrescriptionOrder();
          },
          { capture: true }
        );
      }

      /**
       * Shows/updates a live upload-progress line right after the Add
       * to Cart button. Created lazily and reused across renders so
       * repeated progress events don't thrash the DOM.
       */
      setUploadProgressUI(submitButton, percent) {
        if (!submitButton) return;
        if (!this._uploadProgressEl) {
          const el = document.createElement('p');
          el.setAttribute('data-upload-progress', '');
          el.setAttribute('aria-live', 'polite');
          submitButton.insertAdjacentElement('afterend', el);
          this._uploadProgressEl = el;
        }
        this._uploadProgressEl.textContent = percent == null ? '' : `Uploading your prescription… ${percent}%`;
      }

      clearUploadProgressUI() {
        this._uploadProgressEl?.remove();
        this._uploadProgressEl = null;
      }

      async submitPrescriptionOrder() {
        if (this.submitting) return;

        const productForm = this.productFormElement;
        const submitButton = productForm?.querySelector('[type="submit"]');
        const errorWrapper = productForm?.querySelector('.product-form__error-message-wrapper');
        const errorMessageEl = errorWrapper?.querySelector('.product-form__error-message');

        const frameVariantId = this.readCurrentVariantId();
        const lensVariantId = this.lensVariantMap[this.state.savedPayload.lens.id];
        const cartElement = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.submitting = true;
        submitButton?.classList.add('loading');
        submitButton?.setAttribute('aria-disabled', 'true');
        errorWrapper?.setAttribute('hidden', '');
        this.setUploadProgressUI(submitButton, 0);

        const result = await this._modules.addPrescriptionOrderToCart({
          frameVariantId,
          lensVariantId,
          payload: this.state.savedPayload,
          file: this.state.savedFile,
          cartElement,
          onUploadProgress: (percent) => this.setUploadProgressUI(submitButton, percent),
        });

        this.submitting = false;
        submitButton?.classList.remove('loading');
        submitButton?.removeAttribute('aria-disabled');
        this.clearUploadProgressUI();

        if (!result.ok) {
          // Reuse Dawn's own error-message slot on the product form so
          // this reads exactly like a normal Add to Cart failure —
          // per spec Section 33, never leave the customer stuck.
          if (errorWrapper && errorMessageEl) {
            errorMessageEl.textContent = result.error;
            errorWrapper.removeAttribute('hidden');
          } else {
            console.error('prescription-configurator: cart add failed:', result.error);
          }
          return;
        }

        // Successful add: clear the saved prescription so a repeat
        // Add to Cart click doesn't re-add it, re-enable Buy It Now,
        // and collapse back to the initial "Add Your Prescription" row.
        this.state = emptyState();
        this.setDynamicCheckoutDisabled(false);
        this.render();
      }

      /**
       * Reads the currently selected frame's colour/name from the
       * page's own variant-selects component, so we never duplicate
       * Shopify's variant state (per spec: variants stay the single
       * source of truth for frame selection).
       */
      readCurrentFrameName() {
        const scope = this.closest('product-info') || document;
        const variantJson = scope.querySelector('variant-selects [data-selected-variant]');
        if (!variantJson) return null;

        try {
          const variant = JSON.parse(variantJson.textContent);
          const knownFrames = ['Crystal Clear', 'Stealth Black', 'Phantom Purple', 'Solar Crimson'];
          const match = (variant.options || []).find((value) => knownFrames.includes(value));
          return match || variant.options?.[0] || null;
        } catch (e) {
          console.error('prescription-configurator: could not parse selected variant JSON.', e);
          return null;
        }
      }

      readCurrentVariantId() {
        const scope = this.closest('product-info') || document;
        const variantJson = scope.querySelector('variant-selects [data-selected-variant]');
        if (!variantJson) return null;
        try {
          const variant = JSON.parse(variantJson.textContent);
          return variant?.id ?? null;
        } catch (e) {
          console.error('prescription-configurator: could not parse selected variant JSON.', e);
          return null;
        }
      }

      bindVariantChangeListener() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
          return; // Dawn's global pub/sub isn't available — fail soft
        }
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event?.data?.sectionId !== this.sectionId) return;
          const newFrame = this.readCurrentFrameName();
          if (newFrame === this.currentFrame) return;

          this.currentFrame = newFrame;

          // Frame changed: a saved prescription's lens may no longer
          // be valid for the new frame. Clear it and re-enable Buy It
          // Now rather than silently keeping a mismatched lens
          // attached to a different frame colour.
          if (this.state.savedPayload) {
            this.state = emptyState();
            this.setDynamicCheckoutDisabled(false);
          }
          this.state.selectedLensId = null;
          if (
            this.state.open &&
            (this.state.step === STEPS.LENS_SELECTION ||
              this.state.step === STEPS.UPLOAD ||
              this.state.step === STEPS.REVIEW)
          ) {
            this.state.step = STEPS.LENS_SELECTION;
          }
          this.render();
        });
      }

      // ---- Derived state -------------------------------------------------

      get prescriptionClass() {
        const { od, os } = this.state;
        if (!od.sphere || !os.sphere) return null;
        return this._modules.classifyPrescription({
          odSphere: od.sphere,
          odCylinder: od.cylinder,
          osSphere: os.sphere,
          osCylinder: os.cylinder,
        });
      }

      get availableLensOptions() {
        if (!this.currentFrame) return [];
        return this._modules.getAvailableLensOptions(this.currentFrame, this.prescriptionClass);
      }

      get selectedLens() {
        return this.availableLensOptions.find((opt) => opt.id === this.state.selectedLensId) || null;
      }

      /**
       * Weeks of extra lead time for the currently selected frame's
       * prescription lenses, or null if that frame follows the
       * standard delivery timeline. Used by both the lens-selection
       * step and the review step so the two notices never drift out
       * of sync with each other.
       */
      get currentLeadTimeWeeks() {
        if (!this.currentFrame || !this._modules?.getLensLeadTimeWeeks) return null;
        return this._modules.getLensLeadTimeWeeks(this.currentFrame);
      }

      // ---- Opening / closing the row --------------------------------

      open() {
        this.state.open = true;
        this.state.step = STEPS.PRESCRIPTION_TYPE;
        this.render();
      }

      /** Reopen at Review to edit an already-saved prescription. */
      openForEdit() {
        this.state.open = true;
        this.state.step = STEPS.REVIEW;
        this.render();
      }

      close() {
        this.state.open = false;
        this.render();
      }

      // ---- Navigation ------------------------------------------------

      goToStep(step) {
        this.state.step = step;
        this.state.errors = [];
        this.render();
      }

      next() {
        const { step } = this.state;

        if (step === STEPS.PRESCRIPTION_TYPE) {
          this.goToStep(STEPS.PRESCRIPTION_DETAILS);
          return;
        }

        if (step === STEPS.PRESCRIPTION_DETAILS) {
          const errors = this._modules.validatePrescription({ od: this.state.od, os: this.state.os });
          if (errors.length) {
            this.state.errors = errors;
            this.render();
            return;
          }
          if (this.prescriptionClass === 'out-of-range') {
            this.goToStep(STEPS.OUT_OF_RANGE);
            return;
          }
          this.goToStep(STEPS.PD);
          return;
        }

        if (step === STEPS.PD) {
          const errors = this._modules.validatePd(this.state.pdType, {
            pd: this.state.pd,
            pdRight: this.state.pdRight,
            pdLeft: this.state.pdLeft,
          });
          if (errors.length) {
            this.state.errors = errors;
            this.render();
            return;
          }
          this.goToStep(STEPS.LENS_SELECTION);
          return;
        }

        if (step === STEPS.LENS_SELECTION) {
          if (!this.state.selectedLensId) {
            this.state.errors = ['Please select a lens option before continuing.'];
            this.render();
            return;
          }
          this.goToStep(STEPS.UPLOAD);
          return;
        }

        if (step === STEPS.UPLOAD) {
          if (this.state.compressing) return; // wait for compression to finish before validating
          const errors = this._modules.validatePrescriptionFile(this.state.prescriptionFile);
          if (errors.length) {
            this.state.errors = errors;
            this.render();
            return;
          }
          this.goToStep(STEPS.REVIEW);
          return;
        }
      }

      back() {
        const idx = STEP_ORDER.indexOf(this.state.step);
        if (idx <= 0) {
          this.close();
          return;
        }
        this.goToStep(STEP_ORDER[idx - 1]);
      }

      // ---- Field updates ---------------------------------------------

      setEyeField(eye, field, value) {
        this.state[eye][field] = value;
      }

      setPdType(pdType) {
        this.state.pdType = pdType;
        this.render();
      }

      selectLens(lensId) {
        this.state.selectedLensId = lensId;
        this.render();
      }

      setPrescriptionFile(file) {
        this.state.prescriptionFile = file || null;
        this.state.prescriptionFileName = file ? file.name : '';
        this.render();
      }

      /**
       * Handles a raw file picked in the Upload step: shows a
       * "Preparing your file…" state, compresses/downsamples it if
       * it's a compressible image type, then stores the (possibly
       * smaller) result as the working prescription file.
       */
      async handleFileSelected(rawFile) {
        if (!rawFile) {
          this.setPrescriptionFile(null);
          return;
        }
        this.state.compressing = true;
        this.render();
        const finalFile = await compressPrescriptionImage(rawFile);
        this.state.compressing = false;
        this.setPrescriptionFile(finalFile);
      }

      setConfirmation(field, value) {
        this.state[field] = value;
      }

      // ---- Saving (NOT the same as adding to cart) ----------------------

      buildCompletionPayload() {
        if (!this.state.ageConfirmed || !this.state.detailsConfirmed) return null;
        if (!this.state.prescriptionFile) return null;
        const lens = this.selectedLens;
        if (!lens) return null;

        return {
          collection: this.collection,
          frame: this.currentFrame,
          prescriptionType: 'Single Vision',
          lens: { id: lens.id, name: lens.name, price: lens.price },
          od: { ...this.state.od },
          os: { ...this.state.os },
          pd:
            this.state.pdType === 'single'
              ? { type: 'Single', value: this.state.pd }
              : { type: 'Dual', right: this.state.pdRight, left: this.state.pdLeft },
          prescriptionAgeConfirmed: true,
          prescriptionDetailsConfirmed: true,
          prescriptionFileName: this.state.prescriptionFileName,
        };
      }

      /**
       * Saves the completed prescription into state and collapses the
       * row into its summary view. Does NOT touch the cart — that
       * only happens when the customer clicks the native Add to Cart
       * button afterward (see bindNativeSubmitInterception).
       *
       * The raw File object is kept separately on `savedFile` (not
       * inside `savedPayload`) since File objects aren't JSON-safe and
       * payload is used to build plain-text line-item properties.
       */
      savePrescription() {
        const payload = this.buildCompletionPayload();
        if (!payload) {
          this.state.errors = this.state.prescriptionFile
            ? ['Please confirm both checkboxes above before continuing.']
            : ['Please attach a copy of your prescription before continuing.'];
          this.render();
          return;
        }
        this.state.savedPayload = payload;
        this.state.savedFile = this.state.prescriptionFile;
        this.state.open = false;
        this.setDynamicCheckoutDisabled(true);
        this.render();
      }

      removeSavedPrescription() {
        this.state = emptyState();
        this.setDynamicCheckoutDisabled(false);
        this.render();
      }

      // ---- Rendering -----------------------------------------------------

      render() {
        this.root.innerHTML = '';

        if (this.state.savedPayload && !this.state.open) {
          this.root.appendChild(this.renderSummaryRow());
          return;
        }

        if (!this.state.open) {
          this.root.appendChild(this.renderCollapsedRow());
          return;
        }

        this.root.setAttribute('data-step', this.state.step);
        const panel = document.createElement('div');
        panel.setAttribute('data-configurator-panel', '');

        const stepIndex = STEP_ORDER.indexOf(this.state.step);
        if (stepIndex !== -1) {
          const progress = document.createElement('p');
          progress.setAttribute('data-step-progress', '');
          progress.textContent = `Step ${stepIndex + 1} of ${STEP_ORDER.length}`;
          panel.appendChild(progress);
        }

        if (this.state.errors.length) {
          panel.appendChild(this.renderErrors());
        }

        switch (this.state.step) {
          case STEPS.PRESCRIPTION_TYPE:
            panel.appendChild(this.renderPrescriptionTypeStep());
            break;
          case STEPS.PRESCRIPTION_DETAILS:
            panel.appendChild(this.renderPrescriptionDetailsStep());
            break;
          case STEPS.PD:
            panel.appendChild(this.renderPdStep());
            break;
          case STEPS.LENS_SELECTION:
            panel.appendChild(this.renderLensSelectionStep());
            break;
          case STEPS.UPLOAD:
            panel.appendChild(this.renderUploadStep());
            break;
          case STEPS.REVIEW:
            panel.appendChild(this.renderReviewStep());
            break;
          case STEPS.OUT_OF_RANGE:
            panel.appendChild(this.renderOutOfRangeStep());
            break;
        }

        this.root.appendChild(panel);
      }

      renderCollapsedRow() {
        const wrap = document.createElement('button');
        wrap.type = 'button';
        wrap.setAttribute('data-prescription-row', 'collapsed');
        wrap.innerHTML = `
          <span data-prescription-row-icon aria-hidden="true">Rx</span>
          <span data-prescription-row-label>Add Your Prescription</span>
          <span data-prescription-row-action>Add</span>
        `;
        wrap.addEventListener('click', () => this.open());
        return wrap;
      }

      renderSummaryRow() {
        const { lens } = this.state.savedPayload;
        const wrap = document.createElement('div');
        wrap.setAttribute('data-prescription-row', 'summary');
        wrap.innerHTML = `
          <span data-prescription-row-icon aria-hidden="true">Rx</span>
          <span data-prescription-row-label>${lens.name} — R${lens.price}</span>
          <button type="button" data-action="edit">Edit</button>
          <button type="button" data-action="remove">Remove</button>
        `;
        wrap.querySelector('[data-action="edit"]').addEventListener('click', () => this.openForEdit());
        wrap.querySelector('[data-action="remove"]').addEventListener('click', () => this.removeSavedPrescription());
        return wrap;
      }

      renderErrors() {
        const wrap = document.createElement('div');
        wrap.setAttribute('role', 'alert');
        wrap.dataset.configuratorErrors = '';
        const list = document.createElement('ul');
        this.state.errors.forEach((message) => {
          const li = document.createElement('li');
          li.textContent = message;
          list.appendChild(li);
        });
        wrap.appendChild(list);
        return wrap;
      }

      renderPrescriptionTypeStep() {
        const wrap = document.createElement('div');
        wrap.innerHTML = `
          <h2>Prescription type</h2>
          <div role="radiogroup" aria-label="Prescription type">
            <label>
              <input type="radio" name="rx-type" value="single-vision" checked>
              Single Vision
            </label>
            <label aria-disabled="true">
              <input type="radio" name="rx-type" value="multifocal" disabled>
              Multifocal / Progressive — Coming Soon
            </label>
          </div>
          <button type="button" data-action="back">Cancel</button>
          <button type="button" data-action="next">Continue</button>
        `;
        this.state.prescriptionType = 'single-vision';
        wrap.querySelector('[data-action="back"]').addEventListener('click', () => this.back());
        wrap.querySelector('[data-action="next"]').addEventListener('click', () => this.next());
        return wrap;
      }

      formatDiopter(value) {
        const num = Number(value);
        const sign = num > 0 ? '+' : '';
        return `${sign}${num.toFixed(2)}`;
      }

      buildSelect(name, options, formatter, currentValue, placeholder) {
        const opts = [`<option value="">${placeholder}</option>`]
          .concat(options.map((v) => `<option value="${v}"${String(v) === String(currentValue) ? ' selected' : ''}>${formatter(v)}</option>`))
          .join('');
        return `<select data-field-select="${name}">${opts}</select>`;
      }

      renderPrescriptionDetailsStep() {
        const wrap = document.createElement('div');
        const sphereOpts = this._modules.sphereStepOptions();
        const cylOpts = this._modules.cylinderStepOptions();
        const axisOpts = this._modules.axisStepOptions();

        const eyeFieldsHtml = (eye, label) => `
          <fieldset>
            <legend>${label}</legend>
            <label>SPH ${this.buildSelect(`${eye}-sphere`, sphereOpts, this.formatDiopter, this.state[eye].sphere, 'Select…')}</label>
            <label>CYL ${this.buildSelect(`${eye}-cylinder`, cylOpts, this.formatDiopter, this.state[eye].cylinder, 'Select…')}</label>
            <label>Axis ${this.buildSelect(`${eye}-axis`, axisOpts, (v) => String(v), this.state[eye].axis, '—')}</label>
          </fieldset>
        `;

        wrap.innerHTML = `
          <h2>Enter your prescription</h2>
          <p>Select the values exactly as shown on your optometrist's prescription.</p>
          ${eyeFieldsHtml('od', 'Right Eye — OD')}
          ${eyeFieldsHtml('os', 'Left Eye — OS')}
          <button type="button" data-action="back">Back</button>
          <button type="button" data-action="next">Continue</button>
        `;

        wrap.querySelectorAll('select[data-field-select]').forEach((select) => {
          const [eye, field] = select.dataset.fieldSelect.split('-');
          select.addEventListener('change', () => this.setEyeField(eye, field, select.value));
        });
        wrap.querySelector('[data-action="back"]').addEventListener('click', () => this.back());
        wrap.querySelector('[data-action="next"]').addEventListener('click', () => this.next());
        return wrap;
      }

      renderPdStep() {
        const wrap = document.createElement('div');
        wrap.innerHTML = `
          <h2>Pupillary distance (PD)</h2>
          <details>
            <summary>What is PD?</summary>
            <p>PD is the distance between the centres of your pupils, used to position your lenses correctly. It's usually printed on your prescription, or your optometrist can provide it.</p>
          </details>

          <div role="radiogroup" aria-label="PD measurement type">
            <label>
              <input type="radio" name="pd-type" value="single">
              Single PD
            </label>
            <label>
              <input type="radio" name="pd-type" value="dual">
              I have separate PD measurements for each eye
            </label>
          </div>

          <div data-pd-single-fields hidden>
            <label>PD (mm) <input type="number" step="0.5" data-field="pd"></label>
          </div>
          <div data-pd-dual-fields hidden>
            <label>Right PD (mm) <input type="number" step="0.5" data-field="pdRight"></label>
            <label>Left PD (mm) <input type="number" step="0.5" data-field="pdLeft"></label>
          </div>

          <button type="button" data-action="back">Back</button>
          <button type="button" data-action="next">Continue</button>
        `;

        wrap.querySelectorAll('input[name="pd-type"]').forEach((input) => {
          input.checked = this.state.pdType === input.value;
          input.addEventListener('change', () => this.setPdType(input.value));
        });

        const singleFields = wrap.querySelector('[data-pd-single-fields]');
        const dualFields = wrap.querySelector('[data-pd-dual-fields]');
        singleFields.hidden = this.state.pdType !== 'single';
        dualFields.hidden = this.state.pdType !== 'dual';

        wrap.querySelector('[data-field="pd"]').value = this.state.pd;
        wrap.querySelector('[data-field="pd"]').addEventListener('input', (e) => {
          this.state.pd = e.target.value;
        });
        wrap.querySelector('[data-field="pdRight"]').value = this.state.pdRight;
        wrap.querySelector('[data-field="pdRight"]').addEventListener('input', (e) => {
          this.state.pdRight = e.target.value;
        });
        wrap.querySelector('[data-field="pdLeft"]').value = this.state.pdLeft;
        wrap.querySelector('[data-field="pdLeft"]').addEventListener('input', (e) => {
          this.state.pdLeft = e.target.value;
        });

        wrap.querySelector('[data-action="back"]').addEventListener('click', () => this.back());
        wrap.querySelector('[data-action="next"]').addEventListener('click', () => this.next());
        return wrap;
      }

      renderLensSelectionStep() {
        const wrap = document.createElement('div');
        const options = this.availableLensOptions;
        const isThinned = this.prescriptionClass === 'thinned';
        const leadTimeWeeks = this.currentLeadTimeWeeks;

        const optionsHtml = options
          .map(
            (opt) => `
            <label data-lens-option>
              <input type="radio" name="lens-option" value="${opt.id}">
              <span data-lens-name>${opt.name}</span>
              <span data-lens-price>R${opt.price}</span>
              <span data-lens-description>${opt.description}</span>
            </label>
          `
          )
          .join('');

        wrap.innerHTML = `
          <h2>Choose your prescription lenses</h2>
          ${
            isThinned
              ? `<p data-thinned-notice><strong>Your prescription requires a thinned lens</strong><br>Based on the prescription entered, our standard lenses are not suitable. A thinned lens matched to your frame is available below.</p>`
              : ''
          }
          ${
            leadTimeWeeks
              ? `<p data-lead-time-notice>${this.currentFrame} prescription lenses are individually matched and ground to order — please allow up to ${leadTimeWeeks} week${leadTimeWeeks === 1 ? '' : 's'} for preparation before your order ships.</p>`
              : ''
          }
          <div role="radiogroup" aria-label="Lens options">
            ${optionsHtml}
          </div>
          <button type="button" data-action="back">Back</button>
          <button type="button" data-action="next">Continue</button>
        `;

        wrap.querySelectorAll('input[name="lens-option"]').forEach((input) => {
          input.checked = this.state.selectedLensId === input.value;
          input.addEventListener('change', () => this.selectLens(input.value));
        });
        wrap.querySelector('[data-action="back"]').addEventListener('click', () => this.back());
        wrap.querySelector('[data-action="next"]').addEventListener('click', () => this.next());
        return wrap;
      }

      renderUploadStep() {
        const wrap = document.createElement('div');
        const hasFile = !!this.state.prescriptionFile;
        const { compressing } = this.state;

        wrap.innerHTML = `
          <h2>Attach your prescription</h2>
          <p>Please upload a clear photo or scan of your prescription so Vision Technology can verify it against the details you entered. Accepted formats: JPG, PNG, PDF.</p>

          ${
            compressing
              ? `<p data-upload-status>Preparing your file…</p>`
              : hasFile
              ? `<div data-upload-selected>
                   <span data-upload-filename>${this.state.prescriptionFileName}</span>
                   <button type="button" data-action="remove-file">Remove</button>
                 </div>`
              : `<label data-upload-dropzone>
                   <span>Choose a file</span>
                   <input type="file" accept="image/jpeg,image/png,image/jpg,application/pdf" data-field="prescription-file">
                 </label>`
          }

          <button type="button" data-action="back">Back</button>
          <button type="button" data-action="next"${compressing ? ' aria-disabled="true"' : ''}>Continue</button>
        `;

        const fileInput = wrap.querySelector('input[data-field="prescription-file"]');
        fileInput?.addEventListener('change', () => {
          const rawFile = fileInput.files?.[0] || null;
          this.handleFileSelected(rawFile);
        });

        const removeButton = wrap.querySelector('[data-action="remove-file"]');
        removeButton?.addEventListener('click', () => this.setPrescriptionFile(null));

        wrap.querySelector('[data-action="back"]').addEventListener('click', () => this.back());
        wrap.querySelector('[data-action="next"]').addEventListener('click', () => this.next());
        return wrap;
      }

      renderReviewStep() {
        const wrap = document.createElement('div');
        const lens = this.selectedLens || this.state.savedPayload?.lens;
        const { od, os } = this.state;
        const pdDisplay =
          this.state.pdType === 'single'
            ? `${this.state.pd} mm`
            : `Right ${this.state.pdRight} mm / Left ${this.state.pdLeft} mm`;
        const leadTimeWeeks = this.currentLeadTimeWeeks;

        wrap.innerHTML = `
          <h2>Review your prescription</h2>
          <dl>
            <dt>Frame</dt><dd>${this.currentFrame ?? ''}</dd>
            <dt>Lens</dt><dd>${lens ? `${lens.name} — R${lens.price}` : ''}</dd>
            <dt>Right Eye — OD</dt><dd>SPH ${od.sphere} / CYL ${od.cylinder} / Axis ${od.axis || '—'}</dd>
            <dt>Left Eye — OS</dt><dd>SPH ${os.sphere} / CYL ${os.cylinder} / Axis ${os.axis || '—'}</dd>
            <dt>PD</dt><dd>${pdDisplay}</dd>
            <dt>Prescription Document</dt><dd>${this.state.prescriptionFileName || this.state.savedPayload?.prescriptionFileName || ''}</dd>
          </dl>

          ${
            leadTimeWeeks
              ? `<p data-lead-time-notice>Your prescription lenses are custom prepared by Vision Technology. Please allow up to ${leadTimeWeeks} week${leadTimeWeeks === 1 ? '' : 's'} before your order ships — we'll email you as soon as it's on its way.</p>`
              : ''
          }

          <label>
            <input type="checkbox" data-confirm="ageConfirmed">
            I confirm that my prescription is valid and was issued no more than 2 years ago.
          </label>
          <label>
            <input type="checkbox" data-confirm="detailsConfirmed">
            I confirm that the prescription details entered above are correct.
          </label>

          <button type="button" data-action="edit">Edit prescription</button>
          <button type="button" data-action="save">Save Prescription</button>
          <p data-review-note>You'll add this to your cart with the button below once saved.</p>
        `;

        wrap.querySelectorAll('[data-confirm]').forEach((input) => {
          input.checked = this.state[input.dataset.confirm];
          input.addEventListener('change', () => this.setConfirmation(input.dataset.confirm, input.checked));
        });
        wrap.querySelector('[data-action="edit"]').addEventListener('click', () => this.goToStep(STEPS.PRESCRIPTION_DETAILS));
        wrap.querySelector('[data-action="save"]').addEventListener('click', () => this.savePrescription());
        return wrap;
      }

      renderOutOfRangeStep() {
        const wrap = document.createElement('div');
        wrap.innerHTML = `
          <h2>Prescription outside our current range</h2>
          <p>This prescription falls outside the range currently available for NeonConnect prescription lenses.</p>
          <p>Please contact us before placing your order.</p>
          <button type="button" data-action="edit">Edit prescription</button>
        `;
        wrap.querySelector('[data-action="edit"]').addEventListener('click', () => this.goToStep(STEPS.PRESCRIPTION_DETAILS));
        return wrap;
      }
    }
  );
}